import configs from "./configs";
import { createDpopProof } from "./dpop";
import { clearDashboardAuthHint, writeDashboardAuthHint } from "./dashboard-auth-hint";

const VERIFIER_PREFIX = "auth_bridge_verifier:";
const RETURN_TO_PREFIX = "auth_bridge_return_to:";
const REHYDRATE_ATTEMPT_PREFIX = "auth_bridge_rehydrate_attempt:";
const REHYDRATE_ATTEMPT_TTL_MS = 30000;
const BRIDGE_TOKEN_REUSE_SKEW_MS = 60000;
const DEFAULT_BRIDGE_TOKEN_TTL_MS = 5 * 60000;
const POPUP_WIDTH = 420;
const POPUP_HEIGHT = 585;
const POPUP_CLOSED_POLL_MS = 250;
const INSTANT_ACCESS_SID_PATTERN = /^[A-Za-z0-9]{7}$/;

export const DASHBOARD_BRIDGE_TOKEN_EVENT = "onland-metaverse:auth-bridge-token";
export const ACCOUNT_SUSPENDED_BRIDGE_ERROR = "account_suspended";

let dashboardBridgeToken = null;
let inFlightBridgeTokenRenewal = null;

function normalizeBaseUrl(value) {
  if (!value || typeof value !== "string") return null;
  const trimmed = value.trim().replace(/\/+$/, "");
  if (!trimmed) return null;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

export function getAuthBridgeUrl() {
  return normalizeBaseUrl(configs.AUTH_BRIDGE_URL || configs.CUSTOM_API_HOST);
}

export function getAuthBridgeClientId() {
  return configs.AUTH_BRIDGE_CLIENT_ID || "metaverse";
}

export function isDashboardBridgeConfigured() {
  return Boolean(getAuthBridgeUrl());
}

function getRequiredBridgeUrl() {
  const bridgeUrl = getAuthBridgeUrl();
  if (!bridgeUrl) throw new Error("AUTH_BRIDGE_URL is not configured.");
  return bridgeUrl;
}

function dispatchTokenEvent() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(DASHBOARD_BRIDGE_TOKEN_EVENT));
  }
}

function base64Url(bytes) {
  const binary = Array.from(bytes, byte => String.fromCharCode(byte)).join("");
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function randomString() {
  const bytes = new Uint8Array(32);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
    return base64Url(bytes);
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

async function sha256Base64Url(value) {
  if (typeof crypto !== "undefined" && crypto.subtle) {
    const hash = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
    return base64Url(new Uint8Array(hash));
  }
  return value;
}

function isUsableBridgeToken(entry) {
  return Boolean(entry?.token && entry.expiresAtMs - BRIDGE_TOKEN_REUSE_SKEW_MS > Date.now());
}

export function getDashboardBridgeTokenEntry() {
  return isUsableBridgeToken(dashboardBridgeToken) ? dashboardBridgeToken : null;
}

export function getDashboardBridgeToken() {
  return getDashboardBridgeTokenEntry()?.token || null;
}

function expiresAtSecondsFromPayload(payload) {
  const expiresAt = Number(payload.expires_at);
  if (Number.isFinite(expiresAt) && expiresAt > 0) return expiresAt;

  const expiresIn = Number(payload.expires_in);
  if (Number.isFinite(expiresIn) && expiresIn > 0) {
    return Math.floor(Date.now() / 1000) + expiresIn;
  }

  return null;
}

function sessionContextFromPayload(payload) {
  const authenticationMethod = payload?.authentication_method === "instant_access" ? "instant_access" : "standard";
  const instantAccessSid =
    authenticationMethod === "instant_access" && INSTANT_ACCESS_SID_PATTERN.test(String(payload?.instant_access_sid || ""))
      ? String(payload.instant_access_sid)
      : null;

  return { authenticationMethod, instantAccessSid };
}

export function getDashboardBridgeSessionContext() {
  const entry = getDashboardBridgeTokenEntry();

  return entry
    ? {
        authenticationMethod: entry.authenticationMethod,
        instantAccessSid: entry.instantAccessSid
      }
    : null;
}

export function setDashboardBridgeToken(token, expiresAtSeconds, { sessionContext } = {}) {
  const hadToken = Boolean(dashboardBridgeToken);
  const normalizedToken = typeof token === "string" ? token.trim() : "";
  const normalizedSessionContext = sessionContextFromPayload({
    authentication_method: sessionContext?.authenticationMethod,
    instant_access_sid: sessionContext?.instantAccessSid
  });
  dashboardBridgeToken = normalizedToken
    ? {
        token: normalizedToken,
        expiresAtMs:
          Number.isFinite(Number(expiresAtSeconds)) && Number(expiresAtSeconds) > 0
            ? Number(expiresAtSeconds) * 1000
            : Date.now() + DEFAULT_BRIDGE_TOKEN_TTL_MS,
        ...normalizedSessionContext
      }
    : null;
  if (dashboardBridgeToken) {
    writeDashboardAuthHint();
  }
  if (hadToken !== Boolean(dashboardBridgeToken)) dispatchTokenEvent();
}

export function clearDashboardBridgeToken() {
  inFlightBridgeTokenRenewal = null;
  setDashboardBridgeToken(null);
}

export function readDashboardBridgeCallbackState(state) {
  if (typeof sessionStorage === "undefined") return null;
  const codeVerifier = sessionStorage.getItem(`${VERIFIER_PREFIX}${state}`);
  const returnTo = sessionStorage.getItem(`${RETURN_TO_PREFIX}${state}`);
  return codeVerifier && returnTo ? { codeVerifier, returnTo } : null;
}

export function clearDashboardBridgeCallbackState(state) {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.removeItem(`${VERIFIER_PREFIX}${state}`);
  sessionStorage.removeItem(`${RETURN_TO_PREFIX}${state}`);
}

export function clearDashboardBridgeSessionState(
  storage = typeof sessionStorage !== "undefined" ? sessionStorage : null
) {
  if (!storage) return;
  for (let index = storage.length - 1; index >= 0; index -= 1) {
    const key = storage.key(index);
    if (key?.startsWith(VERIFIER_PREFIX) || key?.startsWith(RETURN_TO_PREFIX)) {
      storage.removeItem(key);
    }
  }
}

export async function prepareDashboardBridgeRequest(returnTo = window.location.href) {
  const state = randomString();
  const codeVerifier = randomString();
  const codeChallenge = await sha256Base64Url(codeVerifier);
  const normalizedReturnTo = new URL(returnTo, window.location.href).toString();

  if (typeof sessionStorage !== "undefined") {
    sessionStorage.setItem(`${VERIFIER_PREFIX}${state}`, codeVerifier);
    sessionStorage.setItem(`${RETURN_TO_PREFIX}${state}`, normalizedReturnTo);
  }

  return {
    state,
    codeVerifier,
    codeChallenge,
    returnTo: normalizedReturnTo
  };
}

export async function buildDashboardBridgePopupStartUrl(returnTo = window.location.href) {
  const bridgeRequest = await prepareDashboardBridgeRequest(returnTo);
  const url = new URL("/auth/bridge/start", getRequiredBridgeUrl());
  url.searchParams.set("client", getAuthBridgeClientId());
  url.searchParams.set("return_to", bridgeRequest.returnTo);
  url.searchParams.set("mode", "popup");
  url.searchParams.set("state", bridgeRequest.state);
  url.searchParams.set("code_challenge", bridgeRequest.codeChallenge);
  return url.toString();
}

export async function buildDashboardBridgeRedirectStartUrl(returnTo = window.location.href, { prompt } = {}) {
  const bridgeRequest = await prepareDashboardBridgeRequest(returnTo);
  const url = new URL("/auth/bridge/start", getRequiredBridgeUrl());
  url.searchParams.set("client", getAuthBridgeClientId());
  url.searchParams.set("return_to", bridgeRequest.returnTo);
  url.searchParams.set("mode", "redirect");
  if (prompt) url.searchParams.set("prompt", prompt);
  url.searchParams.set("state", bridgeRequest.state);
  url.searchParams.set("code_challenge", bridgeRequest.codeChallenge);
  return url.toString();
}

export function clearDashboardBridgeRehydrationAttempt(returnTo = window.location.href) {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.removeItem(`${REHYDRATE_ATTEMPT_PREFIX}${returnTo}`);
}

export async function startDashboardBridgeRedirectRehydration(returnTo = window.location.href) {
  if (typeof window === "undefined" || typeof sessionStorage === "undefined") return false;

  const normalizedReturnTo = new URL(returnTo, window.location.href).toString();
  const attemptKey = `${REHYDRATE_ATTEMPT_PREFIX}${normalizedReturnTo}`;
  const lastAttemptValue = sessionStorage.getItem(attemptKey);
  const lastAttempt = Number(lastAttemptValue);
  const now = Date.now();

  if (lastAttemptValue !== null && Number.isFinite(lastAttempt) && now - lastAttempt < REHYDRATE_ATTEMPT_TTL_MS) {
    return false;
  }

  sessionStorage.setItem(attemptKey, String(now));
  window.location.assign(await buildDashboardBridgeRedirectStartUrl(normalizedReturnTo, { prompt: "none" }));
  return true;
}

export async function renewDashboardBridgeToken() {
  const cachedToken = getDashboardBridgeToken();
  if (cachedToken) return cachedToken;
  if (!inFlightBridgeTokenRenewal) {
    const url = new URL("/auth/bridge/token", getRequiredBridgeUrl());
    const headers = { Accept: "application/json", "Content-Type": "application/json" };
    headers.DPoP = await createDpopProof({ url, method: "POST" });

    inFlightBridgeTokenRenewal = fetch(url, {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify({ client: getAuthBridgeClientId() })
    })
      .then(async response => {
        if (response.status === 204 || response.status === 401 || !response.ok) {
          clearDashboardBridgeToken();
          return null;
        }
        const payload = await response.json();
        const token = typeof payload.access_token === "string" ? payload.access_token : "";
        if (!token) {
          clearDashboardBridgeToken();
          return null;
        }
        setDashboardBridgeToken(token, expiresAtSecondsFromPayload(payload), {
          sessionContext: sessionContextFromPayload(payload)
        });
        return token;
      })
      .finally(() => {
        inFlightBridgeTokenRenewal = null;
      });
  }
  return inFlightBridgeTokenRenewal;
}

export async function exchangeDashboardBridgeCode(code, state, codeVerifier) {
  const url = new URL("/nri/api/auth/bridge/exchange", getRequiredBridgeUrl());
  const headers = { Accept: "application/json", "Content-Type": "application/json" };
  headers.DPoP = await createDpopProof({ url, method: "POST" });

  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify({ client: getAuthBridgeClientId(), code, code_verifier: codeVerifier })
  });
  if (!response.ok) {
    const error = new Error(`Auth bridge exchange failed: ${response.status}`);
    error.status = response.status;
    throw error;
  }
  const payload = await response.json();
  const token = typeof payload.access_token === "string" ? payload.access_token : "";
  if (!token) throw new Error("Auth bridge exchange returned an empty token.");
  setDashboardBridgeToken(token, expiresAtSecondsFromPayload(payload), {
    sessionContext: sessionContextFromPayload(payload)
  });
  return token;
}

function removeBridgeQueryParams() {
  if (typeof window === "undefined" || !window.history?.replaceState) return;
  const url = new URL(window.location.href);
  url.searchParams.delete("code");
  url.searchParams.delete("state");
  url.searchParams.delete("error");
  window.history.replaceState(window.history.state, "", url.toString());
}

export async function completeDashboardBridgeRedirectCallback() {
  if (typeof window === "undefined") return { status: "idle" };

  const url = new URL(window.location.href);
  const code = url.searchParams.get("code") || "";
  const state = url.searchParams.get("state") || "";
  const bridgeError = url.searchParams.get("error") || "";
  if (!state) return { status: "idle" };

  const callbackState = readDashboardBridgeCallbackState(state);
  if (!callbackState) return { status: "idle" };

  try {
    if (bridgeError === ACCOUNT_SUSPENDED_BRIDGE_ERROR) {
      clearDashboardAuthHint();
      logoutWithDashboardBridge(callbackState.returnTo);
      return { status: "suspended", returnTo: callbackState.returnTo };
    }

    if (!code) {
      clearDashboardAuthHint();
      return { status: "no_session", returnTo: callbackState.returnTo };
    }

    const token = await exchangeDashboardBridgeCode(code, state, callbackState.codeVerifier);
    return { status: "authenticated", token, returnTo: callbackState.returnTo };
  } finally {
    clearDashboardBridgeRehydrationAttempt(callbackState.returnTo);
    clearDashboardBridgeCallbackState(state);
    removeBridgeQueryParams();
  }
}

export function buildDashboardBridgePopupFeatures(metrics = typeof window !== "undefined" ? window : {}) {
  const screenX = Number.isFinite(metrics.screenX) ? Number(metrics.screenX) : 0;
  const screenY = Number.isFinite(metrics.screenY) ? Number(metrics.screenY) : 0;
  const viewportWidth = Number.isFinite(metrics.innerWidth)
    ? Number(metrics.innerWidth)
    : Number.isFinite(metrics.outerWidth)
    ? Number(metrics.outerWidth)
    : POPUP_WIDTH;
  const viewportHeight = Number.isFinite(metrics.innerHeight)
    ? Number(metrics.innerHeight)
    : Number.isFinite(metrics.outerHeight)
    ? Number(metrics.outerHeight)
    : POPUP_HEIGHT;
  const left = Math.max(0, Math.round(screenX + (viewportWidth - POPUP_WIDTH) / 2));
  const top = Math.max(0, Math.round(screenY + (viewportHeight - POPUP_HEIGHT) / 2));

  return ["popup=yes", `width=${POPUP_WIDTH}`, `height=${POPUP_HEIGHT}`, `left=${left}`, `top=${top}`].join(",");
}

export async function loginWithDashboardBridgePopup(returnTo = window.location.href) {
  const bridgeUrl = getRequiredBridgeUrl();
  const startUrl = await buildDashboardBridgePopupStartUrl(returnTo);

  const message = await new Promise((resolve, reject) => {
    const popup = window.open(startUrl, "onland-auth-bridge", buildDashboardBridgePopupFeatures());
    if (!popup) {
      reject(Object.assign(new Error("auth_popup_blocked"), { code: "auth_popup_blocked" }));
      return;
    }

    const expectedOrigin = new URL(bridgeUrl).origin;
    const expectedState = new URL(startUrl).searchParams.get("state") || "";
    let timeout = null;
    let closedPoll = null;
    function cleanup(messageListener) {
      window.clearTimeout(timeout);
      window.clearInterval(closedPoll);
      window.removeEventListener("message", messageListener);
    }

    function onMessage(event) {
      if (event.origin !== expectedOrigin) {
        return;
      }

      if (
        event.data?.type === "onland.auth_bridge.error" &&
        event.data?.code === ACCOUNT_SUSPENDED_BRIDGE_ERROR &&
        String(event.data?.state || "") === expectedState
      ) {
        cleanup(onMessage);
        popup.close();
        logoutWithDashboardBridge(returnTo);
        reject(
          Object.assign(new Error(ACCOUNT_SUSPENDED_BRIDGE_ERROR), {
            code: ACCOUNT_SUSPENDED_BRIDGE_ERROR,
            popup
          })
        );
        return;
      }

      if (event.data?.type !== "onland.auth_bridge.code") {
        return;
      }

      cleanup(onMessage);
      popup.close();
      resolve({
        code: String(event.data.code || ""),
        state: String(event.data.state || "")
      });
    }

    timeout = window.setTimeout(() => {
      cleanup(onMessage);
      popup.close();
      reject(Object.assign(new Error("auth_popup_timeout"), { code: "auth_popup_timeout", popup }));
    }, Number(configs.AUTH0_POPUP_TIMEOUT_SECONDS || 300) * 1000);

    closedPoll = window.setInterval(() => {
      if (!popup.closed) return;

      cleanup(onMessage);
      reject(Object.assign(new Error("auth_popup_closed"), { code: "auth_popup_closed", popup }));
    }, POPUP_CLOSED_POLL_MS);

    window.addEventListener("message", onMessage);
  });

  const callbackState = readDashboardBridgeCallbackState(message.state);
  if (!callbackState) {
    throw new Error("Auth bridge callback state is missing.");
  }

  try {
    return await exchangeDashboardBridgeCode(message.code, message.state, callbackState.codeVerifier);
  } finally {
    clearDashboardBridgeCallbackState(message.state);
  }
}

export function buildDashboardBridgeLogoutUrl(returnTo = window.location.href) {
  const url = new URL("/logout", getRequiredBridgeUrl());
  url.searchParams.set("client", getAuthBridgeClientId());
  url.searchParams.set("return_to", returnTo);
  return url.toString();
}

export function logoutWithDashboardBridge(returnTo = window.location.href) {
  clearDashboardBridgeToken();
  clearDashboardAuthHint();
  clearDashboardBridgeSessionState();
  window.location.assign(buildDashboardBridgeLogoutUrl(returnTo));
}
